import MapScale from '@/Enums/Map/MapScale.enum';
import MultiMetricGraphIndex from '@/Enums/Report/MultiMetricGraphIndex.enum';
import TotalSlideIndex from '@/Enums/Report/TotalSlideIndex.enum';
import ReportingTimeBucket from '@/Enums/ReportingDashboard/ReportingTimeBucket.enum';
import TotalMetric from '@/Enums/ReportingDashboard/TotalMetric.enum';
import type { IGraphMetricOption } from '@/Interfaces/Graphs/MultiMetricGraph.interface';
import type { ReportViewType } from '@/types/ReportingDashboard/ReportViewType.type';
import type { GridPaginationModel } from '@mui/x-data-grid-pro';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface ISharedPaginationModel extends Pick<GridPaginationModel, 'pageSize'> {}

export type ISharedTotalsTabs = Record<TotalSlideIndex, TotalMetric | null>;
export type ISharedGraphMetricOptions = Record<MultiMetricGraphIndex, IGraphMetricOption | null>;

interface IState {
  printMode: boolean;
  printAllEnabled: boolean;
  tablesToPrint: ReportViewType[];
  mapScale: MapScale;
  timeBucket: ReportingTimeBucket;
  sharedDateGraphPaginationModel: ISharedPaginationModel;
  sharedGridPaginationModel: ISharedPaginationModel;
  sharedCampaignGraphPaginationModel: ISharedPaginationModel;
  sharedCreativeGraphPaginationModel: ISharedPaginationModel;
  sharedPublisherGraphPaginationModel: ISharedPaginationModel;
  sharedTotalsTabs: ISharedTotalsTabs;
  sharedGraphMetricOptions: ISharedGraphMetricOptions;
}

const getInitialState = (): IState => ({
  printMode: false,
  printAllEnabled: false,
  tablesToPrint: [],
  mapScale: MapScale.State,
  timeBucket: ReportingTimeBucket.Day,
  sharedDateGraphPaginationModel: { pageSize: 7 },
  sharedGridPaginationModel: { pageSize: 10 },
  sharedCampaignGraphPaginationModel: { pageSize: 10 },
  sharedCreativeGraphPaginationModel: { pageSize: 10 },
  sharedPublisherGraphPaginationModel: { pageSize: 10 },
  sharedTotalsTabs: Object.fromEntries(Object.values(TotalSlideIndex).map(idx => [idx, null])) as ISharedTotalsTabs,
  sharedGraphMetricOptions: Object.fromEntries(
    Object.values(MultiMetricGraphIndex).map(idx => [idx, null]),
  ) as ISharedGraphMetricOptions,
});

export const reportingDashboardPrintPdfOptions = createSlice({
  name: 'reportingDashboardPrintPdfOptions',
  initialState: getInitialState(),
  reducers: {
    resetPrintPdfOptions: () => getInitialState(),
    setPrintMode: (state, { payload }: PayloadAction<boolean>) => {
      state.printAllEnabled = false;
      state.tablesToPrint = [];
      state.printMode = payload;
    },
    setPrintAllEnabled: (state, { payload }: PayloadAction<boolean | undefined>) => {
      state.printMode = false;
      state.tablesToPrint = [];
      state.printAllEnabled = typeof payload !== 'undefined' ? payload : !state.printAllEnabled;
    },
    checkTableToPrintList: (state, { payload }: PayloadAction<ReportViewType>) => {
      if (state.tablesToPrint.includes(payload)) {
        state.tablesToPrint = state.tablesToPrint.filter(table => table !== payload);
        return;
      }

      state.tablesToPrint = [...state.tablesToPrint, payload];
    },
    setReportingMapScale: (state, { payload }: PayloadAction<MapScale>) => {
      state.mapScale = payload;
    },
    setReportingTimeBucket: (state, { payload }: PayloadAction<ReportingTimeBucket>) => {
      state.timeBucket = payload;
    },
    setReportingDateGraphPagination: (state, { payload }: PayloadAction<Partial<ISharedPaginationModel>>) => {
      state.sharedDateGraphPaginationModel = { ...state.sharedDateGraphPaginationModel, ...payload };
    },
    setSharedGridPaginationModel: (state, { payload }: PayloadAction<ISharedPaginationModel>) => {
      state.sharedGridPaginationModel = { ...state.sharedGridPaginationModel, ...payload };
    },
    setSharedCampaignGraphPaginationModel: (state, { payload }: PayloadAction<ISharedPaginationModel>) => {
      state.sharedCampaignGraphPaginationModel = { ...state.sharedCampaignGraphPaginationModel, ...payload };
    },
    setSharedCreativeGraphPaginationModel: (state, { payload }: PayloadAction<ISharedPaginationModel>) => {
      state.sharedCreativeGraphPaginationModel = { ...state.sharedCreativeGraphPaginationModel, ...payload };
    },
    setSharedPublisherGraphPaginationModel: (state, { payload }: PayloadAction<ISharedPaginationModel>) => {
      state.sharedPublisherGraphPaginationModel = { ...state.sharedPublisherGraphPaginationModel, ...payload };
    },
    setSharedTotalsTab: (
      state,
      { payload }: PayloadAction<{ slideIndex: TotalSlideIndex; metric: TotalMetric | null }>,
    ) => {
      state.sharedTotalsTabs[payload.slideIndex] = payload.metric;
    },
    setSharedGraphMetric: (
      state,
      { payload }: PayloadAction<{ graphIndex: MultiMetricGraphIndex; metric: IGraphMetricOption | null }>,
    ) => {
      state.sharedGraphMetricOptions[payload.graphIndex] = payload.metric;
    },
  },
});

export default reportingDashboardPrintPdfOptions.reducer;

export const {
  resetPrintPdfOptions,
  setPrintMode,
  setPrintAllEnabled,
  checkTableToPrintList,
  setReportingMapScale,
  setReportingTimeBucket,
  setReportingDateGraphPagination,
  setSharedGridPaginationModel,
  setSharedCampaignGraphPaginationModel,
  setSharedCreativeGraphPaginationModel,
  setSharedPublisherGraphPaginationModel,
  setSharedTotalsTab,
  setSharedGraphMetric,
} = reportingDashboardPrintPdfOptions.actions;
